import { useState } from "react";
import { Link } from "react-router";
import { useSession } from "../../app/session.tsx";
import { useTrip } from "../../app/trip.tsx";
import { importLocal, importSeed, lookupHotelLocations, restoreBackup, uploadAttachment } from "../../lib/api.ts";
import { createId } from "@shared/ids.ts";

export function EditorPage() {
  const { role } = useSession();
  const { trip, empty, save, refresh, saveState, devicePreviewNow, setDevicePreviewNow } = useTrip();
  const [note, setNote] = useState<string | null>(null);
  const [previewNow, setPreviewNow] = useState(devicePreviewNow ?? "");
  const [attachTo, setAttachTo] = useState(trip?.stays[0]?.id ?? "");
  const [caption, setCaption] = useState("");

  if (role !== "owner") {
    return (
      <main className="page">
        <h1 className="serif">Owner only</h1>
        <p>Family sessions can read everything but cannot edit.</p>
      </main>
    );
  }

  const archived = trip?.events.filter((event) => event.archived) ?? [];

  return (
    <main className="page">
      <h1 className="serif">Edit</h1>
      {empty ? <p>No trip is stored yet. Import the reviewed seed.</p> : null}
      <p className="muted">{saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved." : "Import, restore and trip-wide settings live here. Add a day from Today or a daily plan."}</p>
      <div className="card">
        <h2 className="serif">Hotel locations</h2>
        <p className="muted">Owner lookup repairs known bad fallback pins and can store unconfirmed search candidates. Ordinary trip reads do not write coordinates.</p>
        <button
          className="btn-secondary"
          type="button"
          onClick={() => {
            void lookupHotelLocations()
              .then(async (data) => {
                const rows = (data.results as Array<{ name: string; status: string }> | undefined) ?? [];
                setNote(rows.map((r) => `${r.name}: ${r.status}`).join(" · ") || "Lookup finished.");
                await refresh();
              })
              .catch((e: Error) => setNote(e.message));
          }}
        >
          Look up hotel locations
        </button>
      </div>
      <div className="card">
        <h2 className="serif">Import</h2>
        <p>Upload `trip.seed.json`. The file stays off the public bundle.</p>
        <input
          type="file"
          accept="application/json"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            void file.text().then(async (text) => {
              const seed = JSON.parse(text);
              const preview = (await importSeed(seed, false)) as { counts: { bookings: number; flights: number } };
              setNote(`Preview: ${preview.counts.bookings} bookings, ${preview.counts.flights} flights.`);
              if (confirm("Commit this import?")) {
                await importSeed(seed, true);
                await refresh();
                setNote("Imported.");
              }
            });
          }}
        />
        <button className="btn-secondary" type="button" onClick={() => void importLocal().then(() => refresh()).then(() => setNote("Imported from this machine.")).catch((e) => setNote(e.message))}>
          Import local private seed
        </button>
      </div>
      {trip ? (
        <form
          className="card"
          onSubmit={(e) => {
            e.preventDefault();
            setDevicePreviewNow(previewNow || null);
            setNote(previewNow
              ? "Preview clock saved on this phone only. Family Today is not frozen."
              : "Preview clock cleared on this phone.");
          }}
        >
          <label className="field">
            Preview clock (this phone, owner testing)
            <input value={previewNow ?? ""} onChange={(e) => setPreviewNow(e.target.value)} placeholder="2026-10-31T09:00:00+07:00" />
          </label>
          <p className="muted">This does not change the shared trip. It only affects Today on this device while you are signed in as owner.</p>
          <button className="btn" type="submit">Use preview clock on this phone</button>
        </form>
      ) : null}
      {trip ? (
        <div className="card">
          <h2 className="serif">Attach a document</h2>
          <label className="field">
            Link to
            <select value={attachTo} onChange={(e) => setAttachTo(e.target.value)}>
              {trip.stays.map((stay) => <option key={stay.id} value={stay.id}>{stay.chapter}</option>)}
              {trip.bookings.map((booking) => <option key={booking.id} value={booking.id}>{booking.title ?? booking.roomName ?? booking.id}</option>)}
              {trip.flights.map((flight) => <option key={flight.id} value={flight.id}>{flight.number}</option>)}
            </select>
          </label>
          <input
            type="file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              void uploadAttachment(file, attachTo ? [attachTo] : []).then(() => {
                setNote("Attachment saved.");
                void refresh();
              }).catch((err) => setNote(err.message));
            }}
          />
        </div>
      ) : null}
      {archived.length ? (
        <div className="card">
          <h2 className="serif">Archived day items</h2>
          {archived.map((event) => (
            <div key={event.id} className="row-actions">
              <span>{event.date} · {event.title ?? event.kind}</span>
              <button
                className="btn-ghost"
                type="button"
                onClick={() => void save({
                  ...trip!,
                  events: trip!.events.map((row) => (row.id === event.id ? { ...row, archived: false } : row)),
                })}
              >
                Restore
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <div className="card">
        <h2 className="serif">Restore</h2>
        <input
          type="file"
          accept="application/json"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            void file.text().then(async (text) => {
              const payload = JSON.parse(text);
              const preview = (await restoreBackup(payload, false)) as { bookings: number; revision: number };
              if (confirm(`Restore ${preview.bookings} bookings / revision ${preview.revision}?`)) {
                await restoreBackup(payload, true);
                await refresh();
                setNote("Restored.");
              }
            });
          }}
        />
      </div>
      {trip ? (
        <form
          className="card"
          onSubmit={(e) => {
            e.preventDefault();
            if (!caption) return;
            void save({
              ...trip,
              memories: [
                ...trip.memories,
                { id: createId("mem"), date: new Date().toISOString().slice(0, 10), caption, createdAt: new Date().toISOString() },
              ],
            }).then((result) => {
              if (result.ok) {
                setNote("Memory saved.");
                setCaption("");
              } else {
                setNote(result.message);
              }
            });
          }}
        >
          <h2 className="serif">Daily memory</h2>
          <label className="field">
            Caption
            <input value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={200} />
          </label>
          <button className="btn-secondary" type="submit">Add memory</button>
        </form>
      ) : null}
      <p>{note}</p>
      <Link to="/essentials/review">Review flags</Link>
    </main>
  );
}
